import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { api } from '../../shared/api';
import { useAuth } from '../../shared/auth';
import { todayLocalIso, startOfWeekMonIso, fmtDate } from '../../shared/dates';
import { Icon } from '../../ui/Icon';

export function DashboardPage() {
  const today = todayLocalIso();
  const weekStart = startOfWeekMonIso(today);
  const { user } = useAuth();

  const { data: clients } = useQuery({
    queryKey: ['clients-all'],
    queryFn: () => api.get('/clients').then((r) => r.data),
  });
  const { data: providers } = useQuery({
    queryKey: ['providers-active'],
    queryFn: () => api.get('/providers?activeOnly=true').then((r) => r.data),
  });
  const { data: groups } = useQuery({
    queryKey: ['groups-active'],
    queryFn: () => api.get('/group-sessions?activeOnly=true').then((r) => r.data),
  });
  const { data: dailySheet } = useQuery({
    queryKey: ['daily', today],
    queryFn: () => api.get('/instances/daily-sheet', { params: { date: today } }).then((r) => r.data),
  });
  const { data: utilization } = useQuery({
    queryKey: ['util', weekStart],
    queryFn: () =>
      api.get('/reports/provider-utilization', { params: { weekStart } }).then((r) => r.data),
  });
  const { data: flagged } = useQuery({
    queryKey: ['attendance-flags'],
    queryFn: () =>
      api
        .get('/reports/attendance-flags', { _suppressGlobalError: true } as any)
        .then((r) => r.data),
    enabled: user?.role === 'ADMIN',
  });

  const active = clients?.filter((c: any) => c.status === 'ACTIVE').length ?? 0;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">Dashboard</h1>
        <p className="text-sm text-ink-subtle">{fmtDate(today)}</p>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Active clients" value={active} subtle={`${clients?.length ?? 0} total`} />
        <StatCard label="Providers" value={providers?.length ?? 0} subtle="Active only" />
        <StatCard label="Group sessions" value={groups?.length ?? 0} subtle="Currently active" />
        <StatCard
          label="Today expected"
          value={dailySheet?.totals?.expected ?? 0}
          subtle={`Attended ${dailySheet?.totals?.attended ?? 0} · No-show ${
            dailySheet?.totals?.noShow ?? 0
          }`}
        />
      </div>

      {/* Clients needing follow-up — flagged from per-service attendance */}
      <section className="card overflow-hidden">
        <header className="flex items-center justify-between border-b border-line-subtle px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-amber-500">
              <Icon name="check-square" size={16} />
            </span>
            <h2 className="text-sm font-semibold text-ink">Clients needing follow-up</h2>
            <span className="pill-warn">{flagged?.length ?? 0}</span>
          </div>
          <span className="text-2xs text-ink-subtle">Trailing 28 days · per service</span>
        </header>
        {flagged?.length ? (
          <ul className="divide-y divide-line-subtle">
            {flagged.slice(0, 10).map((f: any) => (
              <li key={f.clientId}>
                <Link
                  to={`/clients/${f.clientId}`}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm transition hover:bg-surface-subtle"
                >
                  <span className="w-12 font-mono text-xs font-semibold text-brand-700">
                    {f.cohortId}
                  </span>
                  <span className="w-44 truncate text-ink">{f.name}</span>
                  <span className="pill-muted">Cohort {f.cohort}</span>
                  <span className="ml-auto flex items-center gap-2">
                    {f.topFlag && (
                      <span
                        className={
                          f.topFlag.severity === 'HIGH'
                            ? 'pill-danger'
                            : f.topFlag.severity === 'MEDIUM'
                              ? 'pill-warn'
                              : 'pill-info'
                        }
                      >
                        {f.topFlag.code.replace(/_/g, ' ').toLowerCase()}
                      </span>
                    )}
                    {f.flagCount > 1 && (
                      <span className="pill-muted">+{f.flagCount - 1} more</span>
                    )}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div className="px-4 py-8 text-center text-sm text-ink-subtle">
            No attendance flags. All active clients are within thresholds.
          </div>
        )}
      </section>

      {/* Provider utilization */}
      <section className="card overflow-hidden">
        <header className="flex items-center justify-between border-b border-line-subtle px-4 py-3">
          <h2 className="text-sm font-semibold text-ink">Resource utilization · this week</h2>
          <Link to="/reports" className="text-2xs font-medium text-brand-700 hover:underline">
            Open full report →
          </Link>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-2xs uppercase tracking-wide text-ink-subtle">
                <th className="px-4 py-2 text-left font-medium">Resource</th>
                <th className="px-4 py-2 text-left font-medium">Role / Type</th>
                <th className="px-4 py-2 text-right font-medium">Hours</th>
                <th className="px-4 py-2 text-right font-medium">Capacity</th>
                <th className="px-4 py-2 text-right font-medium">Util.</th>
                <th className="px-4 py-2 text-right font-medium">Clients</th>
              </tr>
            </thead>
            <tbody>
              {(utilization ?? []).map((u: any) => (
                <tr key={u.resourceId} className="border-t border-line-subtle">
                  <td className="px-4 py-2 font-medium text-ink">{u.resourceName}</td>
                  <td className="px-4 py-2 text-ink-muted">
                    {u.providerRole ?? u.resourceType ?? '—'}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {u.capMinutes > 0 ? `${(u.weeklyMinutes / 60).toFixed(1)}h` : '—'}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {u.capMinutes > 0
                      ? `${(u.capMinutes / 60).toFixed(0)}h`
                      : `cap ${u.capacity ?? '—'}`}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {u.capMinutes > 0 ? (
                      <span
                        className={clsx(
                          u.utilizationPct > 95
                            ? 'pill-danger'
                            : u.utilizationPct > 90
                              ? 'pill-warn'
                              : 'pill-info',
                        )}
                      >
                        {u.utilizationPct.toFixed(0)}%
                      </span>
                    ) : (
                      <span className="pill-muted">N/A</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{u.uniqueClients}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function StatCard({
  label,
  value,
  subtle,
}: {
  label: string;
  value: number | string;
  subtle?: string;
}) {
  return (
    <div className="card p-4">
      <div className="text-2xs font-medium uppercase tracking-wider text-ink-subtle">{label}</div>
      <div className="mt-1 text-3xl font-semibold tabular-nums text-ink">{value}</div>
      {subtle && <div className="mt-1 text-2xs text-ink-subtle">{subtle}</div>}
    </div>
  );
}
